import cx from "classnames";
import { useFlags } from "launchdarkly-react-client-sdk";
import type { CSSProperties, PropsWithChildren } from "react";
import { useCallback, useRef, useState } from "react";

import { BlackBackground } from "@/components/video-player/partials/black-background";
import { InfoBar } from "@/components/video-player/partials/info-bar";
import { Overlay } from "@/components/video-player/partials/overlay";
import { hasDisplayDescriptionContent } from "@/content-hub/theme/primitives/video/has-display-description-content";
import { VideoPublicDescription } from "@/content-hub/theme/primitives/video/video-public-description";
import { useOnClickOutside } from "@/hooks/use-on-click-outside";
import { useOnKeyPress } from "@/hooks/use-on-key-press";

import { useCanvasPreviewHeaderOffsetPx } from "./use-canvas-preview-header-offset";
import { useCompactVideoDescriptionLayout } from "./use-compact-video-description-layout";

type PlayerOverlayProps = PropsWithChildren<{
  title?: string;
  onClose: () => void;
  aspectRatio?: string;
  onShareClick?: () => void;
  /** Rich-text description shown below the video in preview / published views. */
  descriptionHtml?: string;
}>;

export const PlayerOverlay = (props: PropsWithChildren<PlayerOverlayProps>) => {
  const {
    onClose,
    title,
    children,
    aspectRatio,
    onShareClick,
    descriptionHtml,
  } = props;
  const ref = useRef(null);
  const [
    descriptionMeasureElement,
    setDescriptionMeasureElement,
  ] = useState<HTMLDivElement | null>(null);
  const descriptionMeasureRef = useCallback((node: HTMLDivElement | null) => {
    setDescriptionMeasureElement(node);
  }, []);
  const flags = useFlags();
  const directVideoSharingEnabled = Boolean(flags.contentHubDirectVideoSharing);
  const shareFromOverlayEnabled = directVideoSharingEnabled && onShareClick;
  const showDescription =
    directVideoSharingEnabled && hasDisplayDescriptionContent(descriptionHtml);
  const topOffsetPx = useCanvasPreviewHeaderOffsetPx();
  const isCompactDescription = useCompactVideoDescriptionLayout(
    descriptionMeasureElement,
    showDescription
  );

  useOnClickOutside(ref, onClose);
  useOnKeyPress("Escape", onClose);

  if (!showDescription) {
    return (
      <Overlay ref={ref} aspectRatio={aspectRatio}>
        <InfoBar
          title={title}
          onClose={onClose}
          onShareClick={shareFromOverlayEnabled ? onShareClick : undefined}
        />
        <BlackBackground>{children}</BlackBackground>
      </Overlay>
    );
  }

  return (
    <Overlay
      ref={ref}
      stackContent
      stackContentCompact={isCompactDescription}
      topOffsetPx={topOffsetPx}
    >
      <InfoBar
        title={title}
        onClose={onClose}
        onShareClick={shareFromOverlayEnabled ? onShareClick : undefined}
      />
      <div
        className={cx(
          "flex w-full flex-col overflow-hidden",
          isCompactDescription ? "shrink-0" : "min-h-0 flex-1"
        )}
      >
        <div
          className="relative w-full shrink-0 bg-black"
          style={getVideoStageStyle(aspectRatio)}
        >
          <div className="absolute inset-0">{children}</div>
        </div>
        {descriptionHtml ? (
          <div
            ref={descriptionMeasureRef}
            className={cx(
              "w-full bg-transparent",
              isCompactDescription
                ? "shrink-0"
                : "flex min-h-0 flex-1 flex-col overflow-hidden"
            )}
          >
            <VideoPublicDescription
              html={descriptionHtml}
              compact={isCompactDescription}
            />
          </div>
        ) : null}
      </div>
    </Overlay>
  );
};

function getVideoStageStyle(aspectRatio: string | undefined): CSSProperties {
  return {
    aspectRatio: aspectRatio ?? "16 / 9",
    maxHeight: "min(52vh, calc(100vh - 13rem))",
  };
}
